import { useState } from "react";
import { C, S } from "../customers/theme";
import { INV_STATUSES, OVERDUE_STATUS, displayStatus } from "../lib/invoices";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const n3 = (x) => Math.round((Number(x) || 0) * 1000) / 1000;
const statusOf = (v) => (v === "overdue" ? OVERDUE_STATUS : INV_STATUSES.find((s) => s.value === v)) || { label: v, color: C.textDim };

export function INVStatusBadge({ invoice, status }) {
  const v = invoice ? displayStatus(invoice) : status;
  const s = statusOf(v);
  return <span style={S.pill(s.color)}>{s.label}</span>;
}

export default function InvoiceView({ invoice, payments = [], creditNotes = [], busy, onClose, onEdit, onMarkSent, onRecordPayment, onVoidPayment, onCreateCreditNote, onOpenCreditNote, onCancel, onDuplicate, onDownload, onEmail }) {
  // SEND-1: shown only after the user clicks the email button on a DRAFT. It is
  // the step that replaces marking the invoice sent automatically.
  const [emailed, setEmailed] = useState(false);
  const lines = (invoice.invoice_lines || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const cust = invoice.customers || {};
  const biz = invoice.business_details || {};
  const bank = invoice.bank_details || {};
  const bands = invoice.vat_breakdown || [];
  const cur = invoice.preferred_currency === "GBP" ? "£" : invoice.preferred_currency === "USD" ? "$" : "€";
  const isDraft = invoice.status === "draft";
  const isCancelled = invoice.status === "cancelled";
  const isPaid = invoice.status === "paid";
  const canPay = !isDraft && !isCancelled && !isPaid;
  const canCredit = !isDraft && !isCancelled;   // credit notes apply to issued invoices

  // ── SEND-1 — the email button ────────────────────────────────────────────
  // Hidden on paid and cancelled: there is nothing to chase and nothing to send.
  // The label is derived from the DISPLAY status, so an unpaid issued invoice
  // past its due date offers a reminder rather than "Email again".
  const shownStatus = displayStatus(invoice);
  const isOverdueNow = shownStatus === "overdue";
  const canEmail = !isPaid && !isCancelled && !!onEmail;
  const emailLabel = isDraft ? "Send by email" : isOverdueNow ? "Send reminder" : "Email again";
  // The filename downloadInvoicePDF actually writes, computed the same way, so the
  // prompt tells the user to attach a file that exists under that exact name.
  const pdfName = `Invoice-${String(invoice.invoice_number || invoice.id || "invoice").replace(/[^\w-]+/g, "_")}.pdf`;
  const clickEmail = () => {
    onEmail(invoice);
    if (isDraft) setEmailed(true);
  };
  const credited = creditNotes.filter((c) => c.status === "issued").reduce((s, c) => s + (Number(c.total_amount) || 0), 0);
  const addr = [biz.address_line1, biz.address_line2, [biz.city, biz.county].filter(Boolean).join(", "), biz.postal_code, biz.country].filter(Boolean);

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 880 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{invoice.invoice_number || "New Invoice"}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3 }}>
              {invoice.customer_name || cust.company_name || "—"}
              {invoice.sales_order_number && <span> · from {invoice.sales_order_number}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <INVStatusBadge invoice={invoice} />
            {isDraft && <button onClick={() => onEdit(invoice)} style={S.btn("secondary")} disabled={busy}>Edit</button>}
            {isDraft && <button onClick={() => onMarkSent(invoice)} style={S.btn()} disabled={busy}>Mark as Sent</button>}
            {canPay && <button onClick={() => onRecordPayment(invoice)} style={S.btn()} disabled={busy}>Record Payment</button>}
            {canCredit && onCreateCreditNote && <button onClick={() => onCreateCreditNote(invoice)} style={S.btn("secondary")} disabled={busy}>Create Credit Note</button>}
            <button onClick={() => onDownload(invoice)} style={S.btn("secondary")} disabled={busy}>⬇ PDF</button>
            {canEmail && <button onClick={clickEmail} style={S.btn()} disabled={busy}>{emailLabel}</button>}
            <button onClick={() => onDuplicate(invoice)} style={S.btn("secondary")} disabled={busy}>Duplicate</button>
            {canPay && <button onClick={() => onCancel(invoice)} style={S.btn("ghost")} disabled={busy}>Cancel invoice</button>}
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        {/* ⛔ SEND-1 — THE INVOICE IS NOT MARKED SENT AUTOMATICALLY, AND MUST NOT BE.
            A mailto: link only opens a draft in the user's mail app. We cannot know
            they pressed send, and an invoice wrongly shown as sent is one nobody
            chases. So the user confirms here, through the existing markSent path
            with its existing errors. */}
        {emailed && isDraft && (
          <div role="status" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: C.primaryDim, border: `1px solid ${C.primary}`, borderRadius: 8, padding: "10px 14px", marginTop: 10, fontSize: 13, lineHeight: 1.5 }}>
            <span aria-hidden="true">✉️</span>
            <div style={{ flex: 1, minWidth: 240 }}>
              {invoice.customer_email ? (
                <>Your email app should have opened with the invoice details. <strong>Attach {pdfName}</strong> (just downloaded) and send it.</>
              ) : (
                <>This invoice has no customer email. Add one under <strong>Edit → Customer</strong>, or type it in your email app. <strong>Attach {pdfName}</strong> (just downloaded) before sending.</>
              )}
            </div>
            <span style={{ color: C.textMuted }}>Sent it?</span>
            <button onClick={() => onMarkSent(invoice)} style={S.btn()} disabled={busy}>Mark as Sent</button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginTop: 14 }}>
          <div>
            <div style={S.sectionTitle}>From</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div><strong>{biz.trading_name || biz.legal_name || "—"}</strong></div>
              {addr.map((l, i) => <div key={i} style={{ color: C.textMuted }}>{l}</div>)}
              {biz.email && <div style={{ color: C.textMuted }}>✉️ {biz.email}</div>}
              {biz.vat_number && <div style={{ color: C.textMuted }}>VAT {biz.vat_number}</div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Bill To</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div><strong>{invoice.customer_name || cust.company_name || "—"}</strong></div>
              {invoice.customer_email && <div style={{ color: C.textMuted }}>✉️ {invoice.customer_email}</div>}
              {invoice.customer_address && <div style={{ color: C.textMuted, whiteSpace: "pre-wrap" }}>{invoice.customer_address}</div>}
              {invoice.customer_vat && <div style={{ color: C.textMuted }}>VAT {invoice.customer_vat}</div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Invoice</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: C.textMuted }}>
              <div>Issued: <span style={{ color: C.text }}>{invoice.issue_date || "—"}</span></div>
              <div>Due: <span style={{ color: displayStatus(invoice) === "overdue" ? C.danger : C.text }}>{invoice.due_date || "—"}</span></div>
              <div>Terms: <span style={{ color: C.text }}>{invoice.payment_terms || "—"}</span></div>
            </div>
          </div>
        </div>

        <div style={S.sectionTitle}>Line Items</div>
        <table style={S.table}>
          <thead><tr>
            {["Description", "SKU", "Qty", "Unit Price", "Disc%", "VAT%", "DRS", "Line Total"].map((h) => <th key={h} style={S.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {lines.length === 0 ? <tr><td colSpan={8} style={S.empty}>No lines</td></tr> : lines.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{l.description || "—"}</td>
                <td style={S.td}>{l.sku || "—"}</td>
                <td style={S.td}>{n3(l.quantity)}</td>
                <td style={S.td}>{money(l.unit_price, cur)}</td>
                <td style={S.td}>{n3(l.discount_percent)}</td>
                <td style={S.td}>{n3(l.vat_rate)}</td>
                <td style={S.td}>{l.drs ? money(l.drs_amount, cur) : "—"}</td>
                <td style={S.td}>{money(l.line_total, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 12, gap: 16, flexWrap: "wrap" }}>
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14, minWidth: 220 }}>
            <div style={{ ...S.label, marginBottom: 8 }}>Payment details</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.7, color: C.textMuted }}>
              {bank.bank_account_name && <div>Account: <span style={{ color: C.text }}>{bank.bank_account_name}</span></div>}
              {bank.bank_name && <div>Bank: <span style={{ color: C.text }}>{bank.bank_name}</span></div>}
              {bank.iban && <div>IBAN: <span style={{ color: C.text }}>{bank.iban}</span></div>}
              {bank.bic && <div>BIC: <span style={{ color: C.text }}>{bank.bic}</span></div>}
              {!bank.iban && !bank.bank_name && <div style={{ color: C.textDim }}>No bank details on this invoice.</div>}
            </div>
          </div>
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14, minWidth: 260 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(invoice.subtotal, cur)}</strong></div>
            {invoice.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(invoice.total_discount, cur)}</strong></div>}
            {bands.map((b) => (
              <div key={b.rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12.5 }}><span style={{ color: C.textMuted }}>VAT @ {b.rate}%</span><span>{money(b.vat, cur)}</span></div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Total VAT</span><strong>{money(invoice.total_vat, cur)}</strong></div>
            {invoice.total_drs > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>DRS deposit</span><strong>{money(invoice.total_drs, cur)}</strong></div>}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(invoice.total_amount, cur)}</span></div>
            {!isDraft && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12.5 }}><span style={{ color: C.textMuted }}>Paid</span><span style={{ color: C.success }}>{money(invoice.amount_paid, cur)}</span></div>
                {credited > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span style={{ color: C.textMuted }}>Credited</span><span style={{ color: C.primary }}>−{money(credited, cur)}</span></div>}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}><span style={{ color: C.textMuted }}>Outstanding</span><span style={{ color: (invoice.amount_outstanding > 0) ? C.warning : C.success }}>{money(invoice.amount_outstanding, cur)}</span></div>
              </>
            )}
          </div>
        </div>

        {!isDraft && (
          <>
            <div style={S.sectionTitle}>Payments</div>
            {payments.length === 0 ? (
              <div style={{ fontSize: 13, color: C.textDim }}>No payments recorded yet — the full balance is outstanding.</div>
            ) : (
              <table style={S.table}>
                <thead><tr>{["Payment", "Date", "Method", "Reference", "Amount", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} style={{ opacity: p.voided ? 0.55 : 1 }}>
                      <td style={S.td}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{p.payment_number}</span></td>
                      <td style={S.td}>{p.payment_date || "—"}</td>
                      <td style={S.td}>{(p.method || "").replace(/_/g, " ")}</td>
                      <td style={S.td}>{p.reference || "—"}</td>
                      <td style={{ ...S.td, textDecoration: p.voided ? "line-through" : "none" }}>{money(p.amount, cur)}</td>
                      <td style={S.td}>
                        {p.voided
                          ? <span style={{ color: C.textDim, fontSize: 12 }}>voided</span>
                          : <button style={S.btn("ghost")} aria-label={`Void ${p.payment_number}`} disabled={busy} onClick={() => onVoidPayment && onVoidPayment(p.id)}>Void</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {!isDraft && creditNotes.length > 0 && (
          <>
            <div style={S.sectionTitle}>Credit Notes</div>
            <table style={S.table}>
              <thead><tr>{["Credit No.", "Date", "Reason", "Status", "Amount"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {creditNotes.map((cn) => (
                  <tr key={cn.id} style={{ cursor: onOpenCreditNote ? "pointer" : "default" }} onClick={() => onOpenCreditNote && onOpenCreditNote(cn.id)}>
                    <td style={S.td}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{cn.credit_note_number || "draft"}</span></td>
                    <td style={S.td}>{cn.issue_date || "—"}</td>
                    <td style={S.td}>{(cn.reason || "—").replace(/_/g, " ")}</td>
                    <td style={S.td}><span style={S.pill(cn.status === "issued" ? C.success : cn.status === "cancelled" ? C.danger : C.textDim)}>{cn.status}</span></td>
                    <td style={S.td}><strong>−{money(cn.total_amount, cur)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {invoice.terms_text && (<><div style={S.sectionTitle}>Terms</div><div style={{ fontSize: 12.5, whiteSpace: "pre-wrap", color: C.textMuted }}>{invoice.terms_text}</div></>)}
        {invoice.notes && (<><div style={S.sectionTitle}>Notes</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{invoice.notes}</div></>)}
      </div>
    </div>
  );
}
